/**
 * World 9 — "The Pale Coast" (Water, levels 81–90). ACT II, chapter 4: a region
 * forgotten so hard it lost its color; the hardest test — remembering what wants
 * to stay forgotten. Levels/caps stamped from World 9's band (81→90). Water pool
 * (Aqua/Yeti/Pinky/Suimon/Fernail/Trixon) gives this world the most species
 * variety. Finale boss (slot 0) is Yeti — the Pale Guardian — who grants the third
 * shard of the First Soul alongside its own Soul Shard; the featured mini-boss
 * is Pinky, the Last Color.
 */

import {
  node,
  giftNode,
  giftTracks,
  giftXp,
  rival,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  AQUA,
  YETI,
  PINKY,
  SUIMON,
  FERNAIL,
  TRIXON,
  VOLTARYX,
  TALION,
  PEFAREON,
  type CampaignWorld,
} from './builders.js';
import { GIFT_SKILL_SUIKA } from '../cardShopConfig.js';
import {
  WORLD9_NODE_STORIES as S,
  WORLD9_ZONE_STORIES as Z,
  WORLD9_TAGLINE,
} from './stories/world9.js';

export const WORLD_9: CampaignWorld = {
  worldId: 'w9',
  name: 'Pale Coast',
  element: 'Water',
  featuredSpeciesId: YETI,
  story: WORLD9_TAGLINE,
  locked: true,
  zones: [
    {
      zoneId: 'z24',
      name: 'Bleached Shore',
      element: 'Water',
      story: Z['z24'],
      nodes: [
        node({
          nodeId: 'z24-n1',
          kind: 'normal',
          rivalName: 'Wisp',
          rivalTitle: 'Fisherman',
          ...S['z24-n1'],
          squad: [rival(FERNAIL, 'Pallor'), rival(AQUA, 'Wan'), rival(SUIMON, 'Bleach')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: FERNAIL,
        }),
        giftNode({
          nodeId: 'z24-g1',
          giver: 'Wisp',
          label: 'Trailside Gift',
          story: 'The fisherman waves you over. His boat has a stripe of blue again.',
          line: "My boat's a little blue again. Take these Soul Shards!",
          reward: { tracks: giftTracks(YETI) },
          portraitSpeciesId: YETI,
        }),
        node({
          nodeId: 'z24-n2',
          kind: 'normal',
          rivalName: 'Nameless Kid',
          rivalTitle: 'No Name',
          ...S['z24-n2'],
          squad: [rival(PINKY, 'Chalk'), rival(TRIXON, 'Faint'), rival(YETI, 'Frost')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PINKY,
        }),
        node({
          nodeId: 'z24-n3',
          kind: 'normal',
          rivalName: 'Mimi',
          rivalTitle: 'Lass',
          ...S['z24-n3'],
          squad: [rival(YETI, 'Hue'), rival(AQUA, 'Tint'), rival(FERNAIL, 'Pigment')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: YETI,
        }),
        node({
          nodeId: 'z24-n5',
          kind: 'normal',
          rivalName: 'Zip',
          rivalTitle: 'Clan Z',
          ...S['z24-n5'],
          squad: [rival(TALION, 'Dash'), rival(AQUA, 'Wan'), rival(SUIMON, 'Bleach')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TALION,
        }),
        node({
          nodeId: 'z24-nw1',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Palefin',
          rivalTitle: 'Wild Suikamon',
          ...S['z24-nw1'],
          squad: [rival(SUIMON, 'Rime'), rival(FERNAIL, 'Ghost'), rival(AQUA, 'Drift')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: SUIMON,
        }),
        node({
          nodeId: 'z24-n4',
          kind: 'zone_boss',
          rivalName: 'White Tide',
          rivalTitle: 'White Tide',
          ...S['z24-n4'],
          squad: [rival(SUIMON, 'Undertow'), rival(TRIXON, 'Surge'), rival(YETI, 'Riptide')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: SUIMON,
        }),
      ],
    },
    {
      zoneId: 'z25',
      name: 'White Silence',
      element: 'Water',
      story: Z['z25'],
      nodes: [
        node({
          nodeId: 'z25-n1',
          kind: 'normal',
          rivalName: 'Quiet Singer',
          rivalTitle: 'Quiet Song',
          ...S['z25-n1'],
          squad: [rival(PINKY, 'Aria'), rival(FERNAIL, 'Hymn'), rival(AQUA, 'Lull')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PINKY,
        }),
        giftNode({
          nodeId: 'z25-g1',
          giver: 'Quiet Singer',
          label: 'Training Charm',
          story: 'The singer hums to some sleepy pups. When she sees you, she smiles. She can sing again, a little.',
          line: 'This charm warms a fading heart. Here.',
          reward: { xp: giftXp(8) },
          portraitSpeciesId: YETI,
        }),
        node({
          nodeId: 'z25-n2',
          kind: 'normal',
          rivalName: 'Lulu',
          rivalTitle: 'Lass',
          ...S['z25-n2'],
          squad: [rival(TRIXON, 'Vanish'), rival(SUIMON, 'Erasure'), rival(YETI, 'Blank')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z25-n3',
          kind: 'normal',
          rivalName: 'Lost Mapper',
          rivalTitle: 'Lost Mapper',
          ...S['z25-n3'],
          squad: [rival(AQUA, 'Compass'), rival(FERNAIL, 'Chart'), rival(PINKY, 'Meridian')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: AQUA,
        }),
        node({
          nodeId: 'z25-n5',
          kind: 'normal',
          rivalName: 'Zap',
          rivalTitle: 'Clan Z',
          ...S['z25-n5'],
          squad: [rival(PEFAREON, 'Shade'), rival(TRIXON, 'Hush'), rival(YETI, 'Pall')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PEFAREON,
        }),
        node({
          nodeId: 'z25-nw2',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Mistcoat',
          rivalTitle: 'Wild Suikamon',
          ...S['z25-nw2'],
          squad: [rival(FERNAIL, 'Drift'), rival(SUIMON, 'Fog'), rival(PINKY, 'Blur')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: FERNAIL,
        }),
        node({
          nodeId: 'z25-n4',
          kind: 'zone_boss',
          rivalName: 'Timmy',
          rivalTitle: 'Youngster',
          ...S['z25-n4'],
          squad: [rival(YETI, 'Herald'), rival(SUIMON, 'Envoy'), rival(TRIXON, 'Whisper')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: YETI,
        }),
      ],
    },
    {
      zoneId: 'z26',
      name: 'Pale Heart',
      element: 'Water',
      story: Z['z26'],
      nodes: [
        node({
          nodeId: 'z26-n1',
          kind: 'normal',
          rivalName: 'Zeck the Boss',
          rivalTitle: 'Clan Z Boss',
          ...S['z26-n1'],
          squad: [rival(VOLTARYX, 'Storm'), rival(PINKY, 'Static'), rival(AQUA, 'Squall')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: VOLTARYX,
        }),
        giftNode({
          nodeId: 'z26-g1',
          giver: 'Shrine Keeper',
          label: 'Soul Token',
          story: 'In the white ahead of you, a pale shrine waits.',
          line: 'The pale shore leaves a big handful of Soul Shards here. Take them!',
          reward: { tracks: giftTracks(YETI, true), suika: GIFT_SKILL_SUIKA },
          portraitSpeciesId: YETI,
        }),
        node({
          nodeId: 'z26-n2',
          kind: 'normal',
          rivalName: 'Aggie',
          rivalTitle: 'Medium',
          ...S['z26-n2'],
          squad: [rival(SUIMON, 'Repose'), rival(YETI, 'Stillness'), rival(TRIXON, 'Numb')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: SUIMON,
        }),
        node({
          nodeId: 'z26-n3',
          kind: 'mini_boss',
          rivalName: 'Last Color',
          rivalTitle: 'Last Color',
          ...S['z26-n3'],
          squad: [rival(PINKY, 'Crimson'), rival(AQUA, 'Azure'), rival(FERNAIL, 'Verdant')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_MINI_BOSS,
          portraitSpeciesId: PINKY,
          soulSpeciesId: PINKY,
        }),
        node({
          nodeId: 'z26-n4',
          kind: 'zone_boss',
          rivalName: 'Fade',
          rivalTitle: 'Pale Guardian',
          ...S['z26-n4'],
          squad: [rival(YETI, 'Fade'), rival(SUIMON, 'Frostkin'), rival(PINKY, 'Snowkin')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: YETI,
          soulSpeciesId: YETI,
        }),
      ],
    },
  ],
};
